import Konva from 'konva';
import * as itemConsts from '../constants/floorPlan';

/**
 * 
 * @param {string} type Type of the zone item.
 * @param {string} color
 * @returns {HTMLImageElement | undefined}
 */
export const getSVGFromURL = (item, color, URLIMAGE="") => {
  let URL = null;
  switch (item.type) {
      case itemConsts.TABLE_TYPE:
          URL = generateTableSvg(item.data.type, item.data.seats, color);
          URL = svgToBase64(URL);
          break;

      //if artefact use SVGs as Images
      case itemConsts.ARTEFACT_TYPE:
          URL = URLIMAGE !== ""? itemConsts.ARTEFACT_SVGs[URLIMAGE]: itemConsts.ARTEFACT_SVGs.BLOCK;
          break;
  }

  return URL;
}

export const svgToBase64 = (svgString) => {
  const base64marker = "data:image/svg+xml;base64,";
  return base64marker + window.btoa(svgString);
}

export const base64ToSvg = (base64Input) => {
  const base64marker = "data:image/svg+xml;base64,";
  base64Input = base64Input.slice(base64marker.length);
  return window.atob(base64Input);
}

/**
 * 
 * @param {Konva.Group} shape - Shape linked to the text.
 * @param {Konva.Group} content - Content to apply the changes.
 * @param {number} rotation - Rotation of the text in degrees (facultative).
 */
export const setContentOffset = (shape, content, rotation = 0) => {

  const image = shape?.attrs.image;

  if (content && image && shape) {

    const text = content.findOne('.tableName');
    const data = content.findOne('#reservationData');
    const title = content.findOne('#reservationTitle');
    const group = content.findOne('#groupIndicator');

    if(text.textHeight <= 0 || text.textWidth <= 0) {
      return;
    }

    data?.position({
      x: text.textWidth * .5,
      y: text.textHeight * 1.6,
    });

    group?.position({
      x: text.textWidth/2,
      y: text.textHeight,
    })

    title?.position({
      x: text.textWidth * .5,
      y: -text.textHeight * .6,
    });

    content.offset({
      x: text.textWidth/2,
      y: text.textHeight/2,
    });

    content.position({
      x: image.width/2,
      y: image.height/2,
    });

    content.rotation(-shape.rotation()-rotation);
  }
}

/**
 * 
 * @param {number} maxX 
 * @param {number} maxY 
 */
export const getRandomCoords = (maxX, maxY) => {
  return {
    x: Math.random() * maxX,
    y: Math.random() * maxY
  }
};

/**
 * 
 * @param {{x: number, y: number, height: number, width: number, rotation: number}} box 
 */
export const getClientRect = (box) => {
  const ang = box.rotation; // in randians

  const cosh = Math.abs(box.height * Math.cos(ang));
  const sinh = Math.abs(box.height * Math.sin(ang));
  
  const cosw = Math.abs(box.width * Math.cos(ang));
  const sinw = Math.abs(box.width * Math.sin(ang));

  return {
    height: cosh + sinw,
    width: sinh + cosw
  }
};

/**
 * 
 * @param {Konva.Node} target 
 * @param {{height: number, width: number}} area
 */
export const checkBounds = (target, area) => {
  const stage = target.getStage();
  
  const rect = target.getClientRect({
    relativeTo: stage,
  });

  const max = { x: 0, y: 0 };
  const min = {...max};

  const corners = getFourPoints(rect);

  for (const corner of corners) {
    for (const axis of ['x', 'y']) {
      if (corner[axis] > max[axis]) 
        max[axis] = corner[axis];
      
      if (corner[axis] < min[axis]) 
        min[axis] = corner[axis];
    }
  }

  const {x, y} = target.position();

  if(min.x < 0)
    target.x(x - min.x);
  if(min.y < 0)
    target.y(y - min.y);
  
  if(max.x > area.width)
    target.x(x - max.x + area.width);
  if(max.y > area.height)
    target.y(y - max.y + area.height);

};

/**
 * 
 * @param {Konva.Stage} target 
 * @param {{height: number, width: number}} area
 * @param {'x' | 'y'} axis
 */
 export const checkStageBounds = (target, prevPos, area, axis = 'x') => {
  
  const rect = target.getClientRect();

  if(axis === 'y') {
    target.x(prevPos.x);

    if(rect.y > 0) 
      target.y(0);
    
    if(area.height - rect.height >= rect.y) 
      target.y(area.height - rect.height);
  }
  else {
    target.y(prevPos.y);

    if(rect.x > 0) 
      target.x(0);
    
    if(area.width - rect.width > rect.x) 
      target.x(area.width - rect.width);
  }

};

/**
 * 
 * @param {Konva.Node} target 
 */
export const snapGrid = (target) => {
  const blocks = itemConsts.PX_PER_M*.125;

  target.x(Math.round(target.x()/blocks)*blocks);
  target.y(Math.round(target.y()/blocks)*blocks);
}

/**
 * @param {{ x: number, y: number, height: number, width: number}} rect - position and size of a box.
 * @param {number} angle - angle in degrees.
 */
export const getFourPoints = (rect, angle = 0, isRadian = false) => {

  angle = isRadian ? angle : angle * (Math.PI/180); // angle in radians 

  const cosA = Math.cos(angle);
  const sinA = Math.sin(angle);

  const points = []
  
  points.push({x: rect.x, y: rect.y});

  let hDiff = sinA * rect.width;
  let wDiff = cosA * rect.width;

  points.push({x: rect.x + wDiff, y: rect.y + hDiff});

  hDiff = cosA * rect.height;
  wDiff = sinA * rect.height;

  points.push({x: rect.x - wDiff, y: rect.y + hDiff});

  points.push({x: points[1].x - wDiff, y: points[1].y + hDiff});

  return points;
}

/**
 * 
 * @param {'RECTANGLE' | 'SQUARE' | 'ROUND' | 'OVAL'} type 
 * @param {number} seats 
 * @returns {string | null}
 */
export function generateTableSvg(type, seats, color="#000", px_per_m=itemConsts.PX_PER_M) {

  seats = parseInt(seats);

  if(seats <= 0)
    return null;

  const OPACITY = 0.8;
  const BG_COLOR = "#FFF";

  let shape = ""; // shape of the table and the chairs
  let containerW = 0; // container width
  let containerH = 0; // container height
  let viewBox = ""; // viewBox of SVG

  
  const seatSection = px_per_m * 1;
  const seatR = seatSection/4;

  /**
   * 
   * @param {number} x 
   * @param {number} y 
   * @returns {string}
   */
  const getSeatSvg = (x = 0, y = 0) => `<circle cx='${x}' cy='${y}' r='${seatR}' opacity='${OPACITY}'/>`;
  const getBackSeatSvg = (x = 0, y = 0) => `<circle cx='${x}' cy='${y}' r='${seatR}' fill='${BG_COLOR}'/>`;

  const to_even = n => n + (n%2);

  switch (type) {

    case "OVAL":
      const RadiusX = Math.ceil(seats/2) / 2;
      const RadiusY = 1 / 2;
      const even_seats = to_even(seats);
      const a = 2*(Math.PI / (even_seats+2));

      const scaledRadiusX = (RadiusX * seatSection) + seats
      const scaledRadiusY = (RadiusY * seatSection) + seats

      containerH = (scaledRadiusY + seatR) * 2;
      containerW = scaledRadiusX * 2;

      viewBox = `0 0 ${containerW} ${containerH}`;

      shape = `<ellipse cx="${containerW/2}" cy="${containerH/2}" rx="${scaledRadiusX}" ry="${scaledRadiusY}" fill='${BG_COLOR}' />`;
      shape += `<ellipse cx="${containerW/2}" cy="${containerH/2}" rx="${scaledRadiusX}" ry="${scaledRadiusY}" opacity='${OPACITY}' />`

      for(let i = 0; i<seats; i++) {
          const past_half = i >= even_seats/2;
          const slice = (a*(i+1)) + (past_half? a: 0) + Math.PI;
          const x = (Math.cos(slice) + 1) * scaledRadiusX;
          const y = (Math.sin(slice) + 1) * scaledRadiusY;
          shape += getSeatSvg(x, y+seatR);
          shape = getBackSeatSvg(x, y+seatR) + shape;
      }
    break;
    case "RECTANGLE":
      const L = Math.ceil(seats/2); // Length of rectangle

      const scaleLength = seatSection * L;

      containerW = scaleLength;
      containerH = seatSection + seatR*2;

      viewBox = `0 0 ${containerW} ${seatSection}`;

      shape = `<rect width='${scaleLength}' height='${seatSection}' fill="${BG_COLOR}"/>`;
      shape += `<rect width='${scaleLength}' height='${seatSection}' opacity='${OPACITY}'/>`;

      for(let i = 0; i<seats; i++) {
        const slice = seatSection/2;
        const x = slice + ((i%L)*seatSection);
        const y = (i>=L)? seatSection : 0;
        shape += getSeatSvg(x, y);
        shape = getBackSeatSvg(x, y) + shape;
      }
        break;

    case "ROUND":
      const Radius = 0.2 + seats/(Math.PI *2);
      const angle = (Math.PI * 2)/seats;

      const scaledRadius = Radius * px_per_m;

      let paddingX = seats <= 2 ? 0: seatR*2;

      containerH = (scaledRadius + seatR) * 2;
      containerW = 2 * scaledRadius + paddingX;

      shape = `<circle cx="${containerW/2}" cy="${containerH/2}" r="${scaledRadius}" fill="${BG_COLOR}"/>`;
      shape += `<circle cx="${containerW/2}" cy="${containerH/2}" r="${scaledRadius}" opacity='${OPACITY}'/>`;
      viewBox = `0 0 ${containerW} ${containerH}`;

      for(let i = 0; i<seats; i++) {
        const slice = (angle*(i+1))-(Math.PI/2);
        const x = ((Math.cos(slice) + 1) * scaledRadius)+paddingX/2;
        const y = ((Math.sin(slice) + 1) * scaledRadius)+seatR;
        shape += getSeatSvg(x, y);
        shape = getBackSeatSvg(x, y) + shape;
      }
      break;

    case "SQUARE":

      const C = Math.ceil(seats/4); // Length of rectangle
      const R = seats%4;

      const scaleSide = C * seatSection; // height of the table

      paddingX = seats <= 2 ? 0: seatR*2;

      containerW = scaleSide + paddingX;
      containerH = scaleSide + seatR*2;
      
      viewBox = `0 0 ${containerW} ${containerH}`;

      shape = `<rect x='${paddingX/2}' y='${seatR}' width='${scaleSide}' height='${scaleSide}' fill='${BG_COLOR}'/>`;
      shape += `<rect x='${paddingX/2}' y='${seatR}' width='${scaleSide}' height='${scaleSide}' opacity='${OPACITY}'/>`;

      for(let i = 0; i<seats; i++) {
          
        const slice = seatSection/2;
        let x = paddingX/2;
        let y = seatR;

        const side = i % 4;
        const index = i - side;
        const offset = (R <= side && R > 0)? slice : 0;

        switch (side) {
          case 0:
            x += slice + (index/2)*slice;
            break;
          case 1:
            y += scaleSide;
            x += scaleSide - slice - ((index/2)*slice) - offset;
            break;
          case 2:
            x += scaleSide;
            y += slice + (index/2)*slice + offset;
            break;
          case 3:
            y += scaleSide - slice - ((index/2)*slice) - offset;
            break;
        }

        shape += getSeatSvg(x, y);
        shape = getBackSeatSvg(x, y) + shape;
      }
      break;
      
    
    default:
      break;
  }

  return `<svg width='${containerW}' height='${containerH}' version='1.1' xmlns='http://www.w3.org/2000/svg' viewBox='${viewBox}'>  <g fill='${color}'> ${shape} </g> </svg>`;
}

/**
 * 
 * @param {Konva.Node} nodeRef 
 * @returns 
 */
export const getCenter = (nodeRef, relativeTo=null) => {
  let [cx, cy] = [0, 0];
  if(nodeRef) {
    const stage = nodeRef.getStage();
    const { x, y, height, width } = nodeRef.getClientRect({ relativeTo: relativeTo ?? stage });
    cx = x + (width/2);
    cy = y + (height/2);
  }
  return [cx, cy];
}


// WEBPACK FOOTER //
// ./src/helpers/floorPlan.js